import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Handles the redirect back from Emergent Google Auth.
 * URL fragment format: #session_id=<token>
 * Exchanges it for a persistent session cookie via the backend, then routes
 * the user into the app.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
    const hasProcessed = useRef(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { refresh } = useAuth() || {};
    const [error, setError] = useState("");

    useEffect(() => {
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        const hash = window.location.hash || "";
        const match = hash.match(/session_id=([^&]+)/);
        const sessionId = match ? decodeURIComponent(match[1]) : null;

        if (!sessionId) {
            setError("No session token found. Redirecting to login...");
            setTimeout(() => navigate("/login", { replace: true }), 1200);
            return;
        }

        (async () => {
            try {
                const { data } = await api.post("/auth/google/session", { session_id: sessionId });
                if (refresh) await refresh();
                toast.success(`Welcome, ${data.user.name}!`);
                // Clean URL fragment and go to dashboard
                window.history.replaceState({}, document.title, window.location.pathname);
                navigate("/", { replace: true, state: { user: data.user } });
            } catch (e) {
                const msg = apiErrorMessage(e);
                setError(msg);
                setTimeout(() => navigate("/login", { replace: true }), 1500);
            }
        })();
    }, [location, navigate, refresh]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]" data-testid="auth-callback">
            <div className="text-center">
                <div className="w-10 h-10 border-4 border-neutral-200 border-t-[#15803D] rounded-full animate-spin mx-auto" />
                <div className="mt-4 text-sm text-neutral-600 font-display">
                    {error || "Signing you in with Google..."}
                </div>
            </div>
        </div>
    );
}
